import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { createNewsFeed, popup, updateNewsFeed } from '../../../utils';
import { NEWS_FEED, VALIDATION, MAX_LENGTH, QUERY_KEYS } from '../../../constants';
import { TextArea } from '../../Admin/DocumentRequest/TextArea';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CircleSpinner from '../../../assets/svg/CircleSpinner';

const DEFAULT_FEEDS = { feeds: '' };

export const NewsFeedAddEdit = ({
    openEditModal,
    setOpenEditModal,
    data,
    isEdit,
    accountId,
    order,
}) => {
    const [feed, setFeed] = useState(DEFAULT_FEEDS);
    const inputRef = useRef(null);
    const queryClient = useQueryClient();

    useEffect(() => {
        if (openEditModal && isEdit && data) {
            setFeed(data);
        } else {
            setFeed(DEFAULT_FEEDS);
        }
        if (openEditModal && inputRef.current) {
            inputRef.current.focus();
        }
    }, [openEditModal, data, isEdit]);

    const handleTextarea = (value) => {
        setFeed((prevData) => ({ ...prevData, feeds: value }));
    };

    const handleClick = async () => {
        if (!feed.feeds) {
            popup('error', VALIDATION.EMPTY_FEED);
            return;
        }
        if (isEdit) {
            editNewsFeedMutation.mutate();
        } else {
            addNewsFeedMutation.mutate();
        }
    };

    const editNewsFeedMutation = useMutation({
        mutationFn: async () =>
            await updateNewsFeed({
                id: data?.id,
                data: { ...feed, feeds: feed?.feeds?.trim() },
            }),
        onSuccess: (res) => {
            popup('success', res.message);
            setOpenEditModal(false);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NEWS_FEED], exact: false });
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const addNewsFeedMutation = useMutation({
        mutationFn: async () =>
            await createNewsFeed({
                data: {
                    ...feed,
                    feeds: feed?.feeds?.trim(),
                    account_id: accountId,
                    order,
                },
            }),
        onSuccess: (res) => {
            popup('success', res.message);
            setOpenEditModal(false);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.NEWS_FEED], exact: false });
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    return (
        <div
            className={`modal custom-modal-doc-cato fade ${openEditModal ? 'show' : ''}`}
            tabIndex={-1}
            aria-labelledby='exampleModalLabel'
            aria-hidden={!openEditModal}
            role='dialog'
            style={{ display: openEditModal ? 'block' : 'none' }}
        >
            <div className='modal-dialog w-100'>
                <div className='modal-content border-0'>
                    <div className='d-flex align-items-center justify-content-between'>
                        <span className='head-title-edit'>
                            {isEdit ? NEWS_FEED.EDIT : NEWS_FEED.ADD}
                        </span>
                        <button
                            type='button'
                            className='btn-close'
                            data-bs-dismiss='modal'
                            aria-label='Close'
                            onClick={() => setOpenEditModal(false)}
                        />
                    </div>
                    <div className='modal-body p-0'>
                        <TextArea
                            name='feeds'
                            placeholder={NEWS_FEED.PLACEHOLDER}
                            note={feed.feeds}
                            setNote={handleTextarea}
                            ref={inputRef}
                            maxLength={MAX_LENGTH.DOCUMENT_NOTES}
                            className='form-control mt-4 py-1'
                        />
                    </div>

                    <div className='d-flex align-items-center justify-content-end modal-btn'>
                        <button
                            type='button'
                            className='ssc-secondary-white-btn'
                            onClick={() => setOpenEditModal(false)}
                            disabled={
                                addNewsFeedMutation.isPending || editNewsFeedMutation.isPending
                            }
                        >
                            Cancel
                        </button>
                        <button
                            type='button'
                            className={`ssc-primary-green-btn py-0 w-auto disabled-hover ${addNewsFeedMutation.isPending || editNewsFeedMutation.isPending ? 'min-w-5' : ''}`}
                            onClick={handleClick}
                            disabled={
                                addNewsFeedMutation.isPending || editNewsFeedMutation.isPending
                            }
                        >
                            {addNewsFeedMutation.isPending || editNewsFeedMutation.isPending ? (
                                <CircleSpinner />
                            ) : (
                                'Save'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

NewsFeedAddEdit.propTypes = {
    openEditModal: PropTypes.bool,
    setOpenEditModal: PropTypes.func,
    data: PropTypes.object,
    isEdit: PropTypes.bool,
    debouncedApiCall: PropTypes.func,
    accountId: PropTypes.string,
    order: PropTypes.number,
};
